<template>
  <div class="topic-container">
    <Spinner
      :active="$fetchState.pending"
      :with-overlay="false" />
    <WrappedHubPage
      v-if="!$fetchState.pending"
      :header="header"
      :paginated-articles="paginatedArticles"
      :email-capture-data="emailCaptureData"
      :articles="articles"
      :show-fetch-button="canFetchMore"
      :category-ad="categoryAd"
      @fetch-next="fetchMoreArticles" />
  </div>
</template>

<script>
import chunk from 'lodash/chunk';
import {
  useRoute, useFetch, ref, useContext, reactive, useMeta, defineComponent
} from '@nuxtjs/composition-api';
import { Spinner } from '@fc/angie-ui';
import { useFetchCategory } from '~/hooks/useFetchCategory';
import { useFetchTopic } from '~/hooks/useFetchTopic';
import { useFetchPaginatedArticles } from '~/hooks/useFetchPaginatedArticles';
import { useFetchBlogPage } from '~/hooks/useFetchBlogPage';
import { WithSelector } from '~/components/wrappers/WithSelector';
import HubPage from '~/components/HubPage.vue';
import { selectHubPage } from '~/selectors/selectHubPage';
import { renderBlockValue } from '~/renderers/renderBlockValue';
import routeJson from '~/nuxtPageRoutes.json';
import { createSocialCardsTags } from '~/utils/socialCardsTags';
import { objectHasKey, withStoryblokHeaderImgResize } from '~/utils/helpers';
import { SITE_TITLE, SITE_DESCRIPTION, SITE_THUMBNAIL } from '~/utils/constants';

export default defineComponent({
  name: 'TopicHubPage',
  components: {
    Spinner,
    WrappedHubPage: WithSelector(HubPage, selectHubPage),
  },
  async validate({ params }) {
    return objectHasKey(routeJson, `/t/${params.topic}`);
  },
  head: {},
  setup() {
    const route = useRoute();
    const { error } = useContext();
    const { rawTopicData, fetchTopic } = useFetchTopic();
    const { rawCategoryData, fetchCategory } = useFetchCategory();
    const {
      getPaginatedArticles,
      canFetchMore,
      newArticles,
    } = useFetchPaginatedArticles();
    const { page, fetchPage } = useFetchBlogPage('topic-hub');

    const initialArticles = ref([]);
    const paginatedArticles = ref([]);
    const slug = route.value.params.topic;
    const categoryAd = ref(null);
    const emailCaptureData = reactive({
      emailCaptureTitle: '',
      emailCaptureDescription: '',
      emailCaptureButtonCta: '',
    });
    const paginationOptions = ref({
      topicUuid: '',
      coverImg: true,
      page: 1,
      perPage: 12,
    });

    async function setPaginationRequirements() {
      await fetchTopic(slug);
      await fetchCategory(rawTopicData.value.content.category.slug);

      paginationOptions.value.topicUuid = rawTopicData.value.uuid;
    }

    async function fetchMoreArticles() {
      if (!paginationOptions.value.topicUuid.length) {
        await setPaginationRequirements();
      }

      paginationOptions.value.page += 1;
      await getPaginatedArticles(paginationOptions.value);
      chunk(newArticles.value, 6).forEach((_chunk) => paginatedArticles.value.push(_chunk));
    }

    useFetch(async () => {
      try {
        await setPaginationRequirements();
        await getPaginatedArticles(paginationOptions.value);
        await fetchPage();

        initialArticles.value = newArticles.value;
        categoryAd.value = renderBlockValue(page, 'hub.topic.ad.small_1');
        emailCaptureData.emailCaptureTitle = renderBlockValue(page, 'hub.topic.email.title');
        emailCaptureData.emailCaptureDescription = renderBlockValue(page, 'hub.topic.email.description');
        emailCaptureData.emailCaptureButtonCta = renderBlockValue(page, 'hub.topic.email.button');
      } catch (err) {
        error({ statusCode: err.response.status, message: err.response.statusText });
      }
    });

    useMeta(() => ({
      title: SITE_TITLE,
      meta: [
        ...createSocialCardsTags({
          title: SITE_TITLE,
          description: SITE_DESCRIPTION,
          imageSrc: withStoryblokHeaderImgResize(SITE_THUMBNAIL),
          pageUrl: process.env.NUXT_ENV_BASE_URL
        })
      ]
    }));

    return {
      header: {
        rawTopicData,
        rawCategoryData,
        rawAllArticles: initialArticles,
      },
      emailCaptureData,
      articles: [],
      categoryAd,
      fetchMoreArticles,
      canFetchMore,
      paginatedArticles,
    };
  }
});
</script>

<style lang="scss" scoped>
.topic-container {
  flex: 1;
  position: relative;
}
</style>
